import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import { useContext, useMemo, useState } from "preact/hooks";

const renderModesOrdered = ["source", "solid", "bricked"] as const;

type RenderMode = (typeof renderModesOrdered)[number];

const defaultRenderMode = "bricked" satisfies RenderMode;

type FasterRenderMode = Exclude<RenderMode, typeof defaultRenderMode>;

type RenderModeValue = {
	readonly activeRenderMode: RenderMode;
	readonly requestRenderMode: (id: string, mode: FasterRenderMode) => void;
	readonly withdrawRenderModeRequest: (id: string) => void;
};

const renderModeContext = createContext<RenderModeValue | undefined>(undefined);

type RenderModeProviderProps = {
	readonly children: ComponentChildren;
};

function RenderModeProvider({ children }: RenderModeProviderProps) {
	const [activeRequests, setActiveRequests] = useState<
		Record<string, RenderMode>
	>({});

	const allRequested = Object.values(activeRequests);
	const renderMode =
		renderModesOrdered.find((r) => allRequested.includes(r)) ??
		defaultRenderMode;

	const value = useMemo(
		(): RenderModeValue => ({
			activeRenderMode: renderMode,
			requestRenderMode(id: string, mode: FasterRenderMode) {
				setActiveRequests((p) => ({
					...p,
					[id]: mode,
				}));
			},
			withdrawRenderModeRequest(id: string) {
				setActiveRequests((p) => {
					const newRequests = { ...p };
					// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
					delete newRequests[id];
					return newRequests;
				});
			},
		}),
		[renderMode],
	);

	return (
		<renderModeContext.Provider value={value}>
			{children}
		</renderModeContext.Provider>
	);
}

function useRenderMode() {
	const value = useContext(renderModeContext);
	if (value === undefined) {
		throw new Error("useRenderModeControls must be used within context");
	}
	return value;
}

export type { RenderMode };
export { useRenderMode, RenderModeProvider };
